import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { SubscriptionStatus } from '@structural-climbing/ui'
import type { GymPlan, Subscription, UpcomingSubscription } from '../plans/types'
import type { ActiveCheckInEntry } from './memberTypes'
import type { Member } from './memberTypes'

export type StatusFilter = 'all' | 'active' | 'expired' | 'none' | 'club_member' | 'desk'

/** A member's child as the list returns it, with the plan they hold at this gym. */
export interface MemberChild {
  id: string
  first_name: string
  last_name: string | null
  subscription_status: SubscriptionStatus | null
  subscription: Subscription | null
  /** The renewal queued behind the child's plan. */
  upcoming_subscription?: UpcomingSubscription | null
}

export interface PaginatedMember extends Member {
  subscription: Subscription | null
  /** The renewal queued behind the member's plan. */
  upcoming_subscription?: UpcomingSubscription | null
  /**
   * Always present, empty for a member with none. On a search that found the
   * member through a child's name, only the matching children.
   */
  children: MemberChild[]
}

export const MEMBERS_PAGE_SIZE = 20
const PAGE_SIZE = MEMBERS_PAGE_SIZE
const SEARCH_DEBOUNCE_MS = 300

// "club_member" isn't a real subscription status — it means "all subscriptions,
// but only club members", so it maps to status=all plus a flag filter.
// "desk" is the same idea: everyone the gym added by hand, whatever their plan.
function toApiParams(statusFilter: StatusFilter): {
  status: string
  flag: string | null
  added: string | null
} {
  if (statusFilter === 'club_member') return { status: 'all', flag: 'is_club_member', added: null }
  if (statusFilter === 'desk') return { status: 'all', flag: null, added: 'desk' }
  return { status: statusFilter, flag: null, added: null }
}

/**
 * One numbered page of the gym's members at a time, searched on the server.
 * `reload` keeps the page on screen, so selling a plan on page 3 does not jump
 * back to page 1.
 */
export function usePaginatedMembers(search: string, statusFilter: StatusFilter) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [members, setMembers] = useState<PaginatedMember[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [plans, setPlans] = useState<GymPlan[]>([])
  const [checkedInMap, setCheckedInMap] = useState<Map<string, ActiveCheckInEntry>>(new Map())
  const [childCheckedInMap, setChildCheckedInMap] = useState<Map<string, ActiveCheckInEntry>>(
    new Map(),
  )

  const abortRef = useRef<AbortController | null>(null)
  const offsetRef = useRef(0)
  // The search and filter in force, for page changes and reloads.
  const currentParamsRef = useRef({ search, statusFilter })

  const fetchPage = useCallback(
    async (searchVal: string, statusVal: StatusFilter, offsetVal: number): Promise<void> => {
      // A newer request replaces any still in flight.
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoading(true)

      try {
        const token = await getAccessTokenSilently()
        const { status, flag, added } = toApiParams(statusVal)
        let at = offsetVal
        // Twice at most: the last person on the last page was removed, so the
        // page asked for is empty and the one before is shown instead.
        for (let attempt = 0; attempt < 2; attempt++) {
          const params = new URLSearchParams({
            offset: String(at),
            limit: String(PAGE_SIZE),
            status,
          })
          if (searchVal) params.set('search', searchVal)
          if (flag) params.set('flag', flag)
          if (added) params.set('added', added)

          const res = await fetch(`${apiUrl}/members?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          })
          if (!res.ok) return
          const data = (await res.json()) as { members: PaginatedMember[]; total: number }
          if (data.members.length === 0 && at > 0 && data.total > 0 && attempt === 0) {
            at = Math.floor((data.total - 1) / PAGE_SIZE) * PAGE_SIZE
            continue
          }
          setMembers(data.members)
          setTotal(data.total)
          setOffset(at)
          offsetRef.current = at
          return
        }
      } catch (err) {
        // Aborted: a newer request is already in flight.
        if (err instanceof Error && err.name === 'AbortError') return
      } finally {
        if (abortRef.current === controller) setLoading(false)
      }
    },
    [getAccessTokenSilently, apiUrl],
  )

  const fetchAncillary = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const [plansRes, checkInsRes] = await Promise.all([
        fetch(`${apiUrl}/gym-plans`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/active-check-ins`, { headers: { Authorization: `Bearer ${token}` } }),
      ])
      if (plansRes.ok) setPlans((await plansRes.json()) as GymPlan[])
      if (checkInsRes.ok) {
        const data = (await checkInsRes.json()) as {
          active_check_ins: {
            id: string
            user_id: string | null
            child_id: string | null
            is_guest: boolean
          }[]
        }
        const parentMap = new Map<string, ActiveCheckInEntry>()
        const childMap = new Map<string, ActiveCheckInEntry>()
        for (const c of data.active_check_ins) {
          if (c.child_id) {
            childMap.set(c.child_id, { checkInId: c.id, isGuest: c.is_guest })
          } else if (c.user_id) {
            parentMap.set(c.user_id, { checkInId: c.id, isGuest: c.is_guest })
          }
        }
        setCheckedInMap(parentMap)
        setChildCheckedInMap(childMap)
      }
    } catch {
      // non-critical — page still usable without plans/check-ins
    }
  }, [getAccessTokenSilently, apiUrl])

  // Debounce the search, then start again from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      currentParamsRef.current = { search, statusFilter }
      offsetRef.current = 0
      void fetchPage(search, statusFilter, 0)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter])

  // Initial ancillary data load
  useEffect(() => {
    void fetchAncillary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goToPage = useCallback(
    (page: number) => {
      const { search: s, statusFilter: sf } = currentParamsRef.current
      void fetchPage(s, sf, (Math.max(1, page) - 1) * PAGE_SIZE)
    },
    [fetchPage],
  )

  const reload = useCallback(async () => {
    const { search: s, statusFilter: sf } = currentParamsRef.current
    await Promise.all([fetchPage(s, sf, offsetRef.current), fetchAncillary()])
  }, [fetchPage, fetchAncillary])

  return {
    members,
    total,
    loading,
    goToPage,
    /** The page on screen, from 1. */
    page: Math.floor(offset / PAGE_SIZE) + 1,
    reload,
    plans,
    checkedInMap,
    childCheckedInMap,
  }
}
